import {
  Actions,
  Button,
  ButtonIcon,
  Dialog,
  Divider,
  Dropdown,
  IconDelete,
  IconEdit,
  Spread,
  Stack,
  Text,
  TextField,
} from 'braid-design-system';
import React from 'react';

import {
  ADMIN_GROUPS,
  ADMIN_ROLE_LABELS,
  ALL_ADMIN_ROLES,
  isValidAdminEmail,
  type AdminRole,
} from '#shared/adminPermissions';
import type { AdminUserRecord } from '#shared/types';
import {
  createAdminUser,
  deleteAdminUser,
  disableAdminUser,
  enableAdminUser,
  fetchAdminUsers,
  resetAdminUserPassword,
  updateAdminUserRole,
} from '#src/api/adminUsers';
import { getAccessToken } from '#src/utils/cognitoAuth';

import { DialogErrorNotice } from '../DialogErrorNotice';

type AdminUsersProps = {
  currentUsername: string | null;
  onError: (message: string | null) => void;
};

const formatStatus = (user: AdminUserRecord): string => {
  switch (user.status) {
    case 'force_change_password':
      return 'Must change password';
    case 'disabled':
      return 'Disabled';
    default:
      return 'Active';
  }
};

export const AdminUsers = ({ currentUsername, onError }: AdminUsersProps) => {
  const [users, setUsers] = React.useState<AdminUserRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [editTarget, setEditTarget] = React.useState<AdminUserRecord | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] =
    React.useState<AdminUserRecord | null>(null);
  const [editRole, setEditRole] = React.useState<AdminRole>(
    ADMIN_GROUPS.contentAdmin,
  );
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState<AdminRole>(
    ADMIN_GROUPS.contentAdmin,
  );
  const [submitting, setSubmitting] = React.useState(false);
  const [dialogError, setDialogError] = React.useState<string | null>(null);
  const [inviteEmailError, setInviteEmailError] = React.useState<
    string | undefined
  >();

  const loadUsers = React.useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    const nextUsers = await fetchAdminUsers(token);
    setUsers(nextUsers);
  }, []);

  React.useEffect(() => {
    loadUsers()
      .catch(() => onError('Failed to load admin users'))
      .finally(() => setLoading(false));
  }, [loadUsers, onError]);

  React.useEffect(() => {
    if (editTarget) {
      setEditRole(editTarget.role ?? ADMIN_GROUPS.contentAdmin);
    }
  }, [editTarget]);

  const withToken = async (action: (token: string) => Promise<void>) => {
    const token = getAccessToken();
    if (!token) {
      const message = 'Session expired. Please log in again.';
      setDialogError(message);
      onError(message);
      return;
    }

    try {
      onError(null);
      setDialogError(null);
      setSubmitting(true);
      await action(token);
      await loadUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Action failed';
      setDialogError(message);
      onError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const isCurrentUser = (user: AdminUserRecord): boolean =>
    currentUsername !== null &&
    (user.username === currentUsername || user.email === currentUsername);

  const openEdit = (user: AdminUserRecord) => {
    setDialogError(null);
    setEditTarget(user);
    setEditRole(user.role ?? ADMIN_GROUPS.contentAdmin);
  };

  const handleInvite = async () => {
    const email = inviteEmail.trim();
    if (!email || !isValidAdminEmail(email)) {
      setInviteEmailError('A valid email address is required');
      return;
    }

    await withToken(async (token) => {
      await createAdminUser(token, {
        email,
        role: inviteRole,
      });
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole(ADMIN_GROUPS.contentAdmin);
    });
  };

  const handleSaveRole = async () => {
    if (!editTarget || editRole === editTarget.role) {
      setEditTarget(null);
      return;
    }

    await withToken(async (token) => {
      await updateAdminUserRole(token, editTarget.username, editRole);
      setEditTarget(null);
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    await withToken(async (token) => {
      await deleteAdminUser(token, deleteTarget.username);
      setDeleteTarget(null);
      setEditTarget(null);
    });
  };

  if (loading) {
    return <Text>Loading admin users…</Text>;
  }

  return (
    <Stack space="medium">
      {users.length === 0 ? (
        <Text>No admin users found.</Text>
      ) : (
        <Stack space="medium">
          {users.map((user, index) => (
            <React.Fragment key={user.username}>
              {index > 0 ? <Divider /> : null}
              <Spread direction="horizontal" space="small" alignY="center">
                <Stack space="xsmall">
                  <Text weight="strong">{user.email}</Text>
                  <Text tone="secondary">
                    {user.role
                      ? ADMIN_ROLE_LABELS[user.role]
                      : 'No role assigned'}{' '}
                    · {formatStatus(user)}
                  </Text>
                </Stack>
                <Actions>
                  <ButtonIcon
                    icon={<IconEdit />}
                    variant="soft"
                    disabled={submitting}
                    onClick={() => openEdit(user)}
                    label="Edit user"
                  />
                  <ButtonIcon
                    icon={<IconDelete />}
                    variant="soft"
                    tone="brandAccent"
                    disabled={submitting || isCurrentUser(user)}
                    onClick={() => {
                      setDialogError(null);
                      setDeleteTarget(user);
                    }}
                    label="Delete user"
                  />
                </Actions>
              </Spread>
            </React.Fragment>
          ))}
        </Stack>
      )}

      <Actions>
        <Button
          tone="formAccent"
          onClick={() => {
            setDialogError(null);
            setInviteEmailError(undefined);
            setInviteOpen(true);
          }}
        >
          Invite user
        </Button>
      </Actions>

      <Dialog
        id="invite-admin-user"
        title="Invite admin user"
        open={inviteOpen}
        onClose={() => {
          if (!submitting) {
            setInviteOpen(false);
          }
        }}
        width="medium"
      >
        <Stack space="medium">
          <DialogErrorNotice message={dialogError} />
          <TextField
            label="Email"
            value={inviteEmail}
            onChange={(event) => {
              setInviteEmail(event.currentTarget.value);
              setInviteEmailError(undefined);
            }}
            message={inviteEmailError}
            tone={inviteEmailError ? 'critical' : undefined}
          />
          <Dropdown
            label="Role"
            value={inviteRole}
            onChange={(event) =>
              setInviteRole(event.currentTarget.value as AdminRole)
            }
          >
            {ALL_ADMIN_ROLES.map((role) => (
              <option key={role} value={role}>
                {ADMIN_ROLE_LABELS[role]}
              </option>
            ))}
          </Dropdown>
          <Text tone="secondary">
            Cognito will email an invitation with a temporary password.
          </Text>
          <Actions>
            <Button
              tone="formAccent"
              loading={submitting}
              onClick={handleInvite}
            >
              Invite
            </Button>
            <Button variant="transparent" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
          </Actions>
        </Stack>
      </Dialog>

      <Dialog
        id="edit-admin-user"
        title="Edit admin user"
        open={editTarget !== null}
        onClose={() => {
          if (!submitting) {
            setEditTarget(null);
          }
        }}
        width="medium"
      >
        {editTarget ? (
          <Stack space="medium">
            <DialogErrorNotice message={dialogError} />
            <Text weight="strong">{editTarget.email}</Text>
            <Text tone="secondary">{formatStatus(editTarget)}</Text>
            <Dropdown
              label="Role"
              value={editRole}
              onChange={(event) =>
                setEditRole(event.currentTarget.value as AdminRole)
              }
              disabled={submitting || isCurrentUser(editTarget)}
            >
              {ALL_ADMIN_ROLES.map((role) => (
                <option key={role} value={role}>
                  {ADMIN_ROLE_LABELS[role]}
                </option>
              ))}
            </Dropdown>
            <Actions>
              <Button
                tone="formAccent"
                loading={submitting}
                onClick={handleSaveRole}
              >
                Save role
              </Button>
              {editTarget.enabled ? (
                <Button
                  variant="ghost"
                  tone="critical"
                  disabled={submitting || isCurrentUser(editTarget)}
                  onClick={() =>
                    withToken(async (token) => {
                      await disableAdminUser(token, editTarget.username);
                      setEditTarget(null);
                    })
                  }
                >
                  Disable
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  disabled={submitting || isCurrentUser(editTarget)}
                  onClick={() =>
                    withToken(async (token) => {
                      await enableAdminUser(token, editTarget.username);
                      setEditTarget(null);
                    })
                  }
                >
                  Enable
                </Button>
              )}
              <Button
                variant="ghost"
                disabled={submitting}
                onClick={() =>
                  withToken((token) =>
                    resetAdminUserPassword(token, editTarget.username),
                  )
                }
              >
                Reset password
              </Button>
              <Button
                variant="ghost"
                tone="critical"
                disabled={submitting || isCurrentUser(editTarget)}
                onClick={() => {
                  setDialogError(null);
                  setDeleteTarget(editTarget);
                }}
              >
                Delete
              </Button>
              <Button
                variant="transparent"
                onClick={() => setEditTarget(null)}
                disabled={submitting}
              >
                Cancel
              </Button>
            </Actions>
          </Stack>
        ) : null}
      </Dialog>

      <Dialog
        id="delete-admin-user"
        title="Delete admin user"
        open={deleteTarget !== null}
        onClose={() => {
          if (!submitting) {
            setDeleteTarget(null);
          }
        }}
        width="medium"
      >
        <Stack space="medium">
          <DialogErrorNotice message={dialogError} />
          <Text>Delete {deleteTarget?.email}? This cannot be undone.</Text>
          <Actions>
            <Button tone="critical" loading={submitting} onClick={handleDelete}>
              Delete user
            </Button>
            <Button variant="transparent" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
          </Actions>
        </Stack>
      </Dialog>
    </Stack>
  );
};
